import React from 'react';
import PropTypes from 'prop-types';
import { BrandColor } from '@styleseat/brand';
import { View, TouchableOpacity } from 'react-native';
import { Box } from '@styleseat/ui';
import BodyText from '../BodyText';
import './ExpandableText.module.scss';

/**
 * ExpandableText
 *
 * components/SeeMore is similar, but uses a third party library that renders
 * the content off screen to accurately determine its height.  This way you
 * can specify the cutoff in pixels.  The downside is the significant performance
 * cost of doing that calculation
 *
 * components/ui/ExpandableText is much more performant, accepting the cutoff
 * in characters rather than pixels.  Wherever this is an acceptable tradeoff,
 * ExpandableText should be used.
 */
export default function ExpandableText({
  text,
  maxLength,
  maxWidth,
  onPress,
  textProps,
}) {
  const [expanded, setExpanded] = React.useState(false);

  const renderedText = React.useMemo(() => {
    const shouldTruncate = !expanded && text.length > maxLength;
    return shouldTruncate ? `${text.substring(0, maxLength)}...` : text;
  }, [
    text,
    maxLength,
    expanded,
  ]);

  const handleReadMoreToggled = () => {
    setExpanded(!expanded);
    onPress(expanded);
  };

  return (
    <View>
      <Box styleName="text-block" maxWidth={maxWidth}>
        <BodyText testID="expandable-text-content" {...textProps}>
          {renderedText}
        </BodyText>

        {text.length > maxLength && (
          <TouchableOpacity
            onPressIn={handleReadMoreToggled}
            testID="expandable-text-toggle"
          >
            <View>
              <BodyText styleName="read-more-link" color={BrandColor.FloraDark}>
                {expanded ? 'Read Less' : 'Read More'}
              </BodyText>
            </View>
          </TouchableOpacity>
        )}
      </Box>
    </View>
  );
}

ExpandableText.propTypes = {
  text: PropTypes.string,
  maxLength: PropTypes.number,
  maxWidth: PropTypes.number,
  onPress: PropTypes.func,
  textProps: PropTypes.object,
};

ExpandableText.defaultProps = {
  text: '',
  maxLength: 80,
  maxWidth: undefined,
  onPress: () => {},
  textProps: null,
};
